import type { StudyDesign } from "@digital-twins/shared-types";
import {
  isMonadicDesign,
  productVersionLabel,
  respondentWordingFor,
  stimulusCutIds,
} from "@digital-twins/shared-types";

/**
 * Sizing math for the review step, expressed the way a marketer edits it: one
 * number per group. The study still persists a total respondent count, so every
 * helper here converts between the two and nothing downstream changes.
 *
 * - `twin_monadic` reads the same twins once per cut, so a group of N costs N
 *   twins and produces N × cuts responses.
 * - everything else has a single group, where per group and total are the same.
 *
 * No design multiplies the respondent count any more: the split-sample design
 * that gave each group its own twins is retired (TRUE_MONADIC_RETIRED), so
 * per-group and total are the same number everywhere.
 */

/**
 * The stepper moves in tens. Quick Study steps by 25 because its range runs to
 * 500; the per-group range here tops out at 100.
 */
export const RESPONDENTS_PER_GROUP_STEP = 10;

export interface PerGroupLimits {
  min: number;
  max: number;
}

export interface SizingSummary {
  /** Distinct twins the study draws. */
  twins: number;
  /** Responses those twins produce, which is what the study bills. */
  responses: number;
  /** True when one twin answers for every group, in separate sessions. */
  sharesTwins: boolean;
}

export interface SizingGroupRow {
  /** Asset title when the questionnaire carries one, else "Version N". */
  name: string;
  respondents: number;
  sharesTwins: boolean;
}

function positiveInt(value: number | null | undefined): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0
    ? Math.floor(value)
    : 0;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asArray(value: unknown): Array<unknown> {
  return Array.isArray(value) ? value : [];
}

/** Groups the design splits the study across. One when there is nothing to split. */
export function groupCount(design: StudyDesign, cutCount: number): number {
  const cuts = positiveInt(cutCount);
  return isMonadicDesign(design) && cuts >= 2 ? cuts : 1;
}

/**
 * Respondents one group costs. Always one group's worth: the only design that
 * needed a distinct twin per cut is retired (TRUE_MONADIC_RETIRED), so per-group
 * and total coincide. Kept as a named function because the conversion helpers
 * below read better with it and a future design could multiply again.
 */
function respondentMultiplier(design: StudyDesign, cutCount: number): number {
  void design;
  void cutCount;
  return 1;
}

/** Per-group count to the total respondent count the study persists and bills. */
export function respondentsFromPerGroup(
  design: StudyDesign,
  cutCount: number,
  perGroup: number,
): number {
  return positiveInt(perGroup) * respondentMultiplier(design, cutCount);
}

/** The reverse: the per-group number behind a stored respondent total. */
export function perGroupFromRespondents(
  design: StudyDesign,
  cutCount: number,
  numRespondents: number | null | undefined,
): number {
  return Math.floor(
    positiveInt(numRespondents) / respondentMultiplier(design, cutCount),
  );
}

/**
 * Per-group bounds derived from the study's respondent bounds, so the stepper
 * can never propose a total the launch validation would reject.
 */
export function perGroupLimits(params: {
  design: StudyDesign;
  cutCount: number;
  minRespondents: number;
  maxRespondents: number;
}): PerGroupLimits {
  const multiplier = respondentMultiplier(params.design, params.cutCount);
  const min = Math.max(1, Math.ceil(positiveInt(params.minRespondents) / multiplier));
  const max = Math.max(min, Math.floor(positiveInt(params.maxRespondents) / multiplier));
  return { min, max };
}

export function clampPerGroup(value: number, limits: PerGroupLimits): number {
  if (!Number.isFinite(value)) {
    return limits.min;
  }
  return Math.max(limits.min, Math.min(limits.max, Math.floor(value)));
}

/**
 * One step up or down, snapping to the bound when a full step would overshoot
 * it. Copied from Quick Study's stepper so the two surfaces behave alike.
 */
export function stepPerGroup(
  value: number,
  direction: 1 | -1,
  limits: PerGroupLimits,
): number {
  const base = clampPerGroup(value, limits);
  return clampPerGroup(base + direction * RESPONDENTS_PER_GROUP_STEP, limits);
}

export function sizingSummary(params: {
  design: StudyDesign;
  cutCount: number;
  perGroup: number;
}): SizingSummary {
  const perGroup = positiveInt(params.perGroup);
  const groups = groupCount(params.design, params.cutCount);
  const sharesTwins = params.design === "twin_monadic" && groups > 1;
  return {
    twins: sharesTwins ? perGroup : perGroup * groups,
    responses: perGroup * groups,
    sharesTwins,
  };
}

export function sizingGroupRows(params: {
  design: StudyDesign;
  cutCount: number;
  perGroup: number;
  labels: Array<string>;
}): Array<SizingGroupRow> {
  const groups = groupCount(params.design, params.cutCount);
  if (groups < 2) {
    return [];
  }
  const sharesTwins = params.design === "twin_monadic";
  return Array.from({ length: groups }, (_, index) => ({
    name: params.labels[index] ?? `Version ${index + 1}`,
    respondents: positiveInt(params.perGroup),
    sharesTwins,
  }));
}

function assetTitles(surveyJson: unknown): Map<string, string> {
  const survey = asRecord(surveyJson);
  const config = asRecord(survey._canonicalConfig);
  const titles = new Map<string, string>();

  const addItems = (items: Array<unknown>, idKey: string, titleKey: string) => {
    for (const raw of items) {
      const item = asRecord(raw);
      const id = String(item[idKey] ?? "").trim();
      const title = String(item[titleKey] ?? "").trim();
      if (id && title && !titles.has(id)) {
        titles.set(id, title);
      }
    }
  };

  // The video catalog is injected python-side after creation, so at review time
  // the titles almost always come from the tested-asset block.
  addItems(asArray(asRecord(config.assetVideoContext).items), "assetId", "title");
  addItems(asArray(asRecord(config.assetContext).items), "assetId", "title");
  addItems(asArray(asRecord(survey.testedAssets).assets), "id", "name");
  return titles;
}

/** Each tested product's typed price, by asset id. */
function productPrices(surveyJson: unknown): Map<string, unknown> {
  const prices = new Map<string, unknown>();
  for (const raw of asArray(asRecord(asRecord(surveyJson).testedAssets).assets)) {
    const asset = asRecord(raw);
    if (asset.type === "product" && asset.price !== undefined) {
      prices.set(String(asset.id ?? ""), asset.price);
    }
  }
  return prices;
}

/**
 * Group names in engine cut order, falling back to "Version N". A priced
 * product's group reads "name · price", so each price version is told apart,
 * its price in the language the survey was compiled in.
 */
export function stimulusCutLabels(surveyJson: unknown): Array<string> {
  const titles = assetTitles(surveyJson);
  const prices = productPrices(surveyJson);
  const language = asRecord(asRecord(surveyJson)._canonicalConfig).contentLanguage;
  const wording = respondentWordingFor(typeof language === "string" ? language : undefined);
  return stimulusCutIds(surveyJson).map((id, index) => {
    const title = titles.get(id);
    return title ? productVersionLabel(title, prices.get(id), wording) : `Version ${index + 1}`;
  });
}
